import React, { ChangeEvent, useState } from "react";

import { LazyQueryExecFunction } from "@apollo/client";
import { ByProjectIdReqInput, Exact, Field, FilterOp, FilterValueType, SortDirection } from "gql/generated/types";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { InputNumber, InputNumberValueChangeParams } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { ToggleButton } from "primereact/togglebutton";
import { TreeSelect } from 'primereact/treeselect';
import { DEFAULT_DATE_FORMAT } from "utils/constants";
import useProjectContext from "../hooks/useProjectContext";
import { DEVICE_CONTAINER, ENUM_OPS, NUM_OPS, OBJECT_CONTAINER } from "../utils/const";
import { FilterField, FilterInternal, LazyState } from "../utils/types";
import { buildNestedFields, findByKey } from "../utils/utils";

type TableFiltersProps<N> = {
  container: typeof DEVICE_CONTAINER | typeof OBJECT_CONTAINER;
  onLazyEvent: React.Dispatch<React.SetStateAction<LazyState>>;
  lazyState: LazyState;
  getNextQuery: LazyQueryExecFunction<N, Exact<{ input: ByProjectIdReqInput }>>;
};

const TableFilters = <N,>({ container, onLazyEvent, lazyState, getNextQuery }: TableFiltersProps<N>) => {
  const { project, projectId } = useProjectContext();
  const [filters, setFilters] = useState<FilterInternal[]>(lazyState.internalFilters ?? []);

  const getOptions = (type: string) => {
    return project?.mdmProjectById.filterInfo.allFields.filter(f => f?.containerType === type);
  }

  const treeOptions = buildNestedFields(container, project?.mdmProjectById.filterInfo.allFields as Field[])

  const addFilter = (e: DropdownChangeParams) => {
    const filter = findByKey(treeOptions, e.value);
    setFilters(prev => [
      ...prev,
      {
        value: null,
        cond: FilterOp.Eq,
        fields: filter?.data ?? [],
        key: filter?.data.map(f => f.key).join(".") ?? "",
        label: filter?.data[0]?.label ?? "",
      },
    ]);
  }

  const onChange = (
    e: ChangeEvent<HTMLInputElement> | DropdownChangeParams | InputNumberValueChangeParams,
    filter: FilterInternal,
    value: any
  ) =>
    setFilters(prev =>
      prev.map(pf => (pf.key === filter.key ? { ...pf, [e.target.name]: value } : pf))
    );

  const getOperators = (filter: FilterField) => {
    switch (filter.inputType) {
      case FilterValueType.Enum:
      case FilterValueType.Boolean:
        return project?.mdmProjectById.filterInfo.allOperators.filter(op => ENUM_OPS.includes(op?.key ?? ""))
      case FilterValueType.Decimal:
      case FilterValueType.Integer:
      case FilterValueType.UtcDate:
        return project?.mdmProjectById.filterInfo.allOperators.filter(op => NUM_OPS.includes(op?.key ?? ""))
      case FilterValueType.String:
      default:
        return project?.mdmProjectById.filterInfo.allOperators
    }
  }

  const renderInputByType = (parentFilter: FilterInternal, filter: FilterField) => {
    switch (filter.inputType) {
      case FilterValueType.Enum:
        return (
          <Dropdown
            value={parentFilter.value}
            options={filter.enum ?? []}
            className="p-inputtext-sm flex-1 p-0"
            optionLabel="label"
            optionValue="key"
            placeholder="Select One"
            name="value"
            onChange={e => onChange(e, parentFilter, e.value)}
          />
        )
      case FilterValueType.UtcDate:
        return (
          <Calendar
            value={parentFilter.value}
            className="p-inputtext-sm flex-1 p-0 calendar-sm"
            name="value"
            onChange={e => onChange(e, parentFilter, e.value)}
            dateFormat={DEFAULT_DATE_FORMAT}
            
            showTime
            hourFormat="24"
          />
        )
      case FilterValueType.Boolean:
        return (
          <ToggleButton
            checked={parentFilter.value === "true"}
            className="p-inputtext-sm flex-1 p-0 m-0"
            name="value"
            onChange={e => onChange(e, parentFilter, e.value.toString())}
            onLabel="true"
            offLabel="false"
          />
        )
      case FilterValueType.Decimal:
      case FilterValueType.Integer:
      return (
        <InputNumber
          value={Number(parentFilter.value) ?? 0}
          className="p-inputtext-sm flex-1 p-0 pl-2"
          placeholder="write value..."
          name="value"
          onValueChange={e => onChange(e, parentFilter, e.value?.toString())}
        />
      );
      case FilterValueType.String: 
      default:
        return (
          <InputText
            value={parentFilter.value ?? ""}
            className="p-inputtext-sm flex-1 p-0 pl-2"
            placeholder="write value..."
            name="value"
            onChange={e => onChange(e, parentFilter, e.target.value)}
          />
        );
    }
  }

  const renderFieldSelector = (filter: FilterInternal, filterIndex: number, currentLevel: number = 0): JSX.Element => {
    const opts = currentLevel === 0 ? [] : getOptions(filter.fields[currentLevel - 1].fieldType!);

    const needRecurse = (filter.fields[currentLevel] && !filter.fields[currentLevel].leaf) || false;
    return (
      <>
        {currentLevel > 0 && (
          <Dropdown
            value={filter.fields[currentLevel]}
            options={opts}
            optionLabel="label"
            placeholder="Select One"
            name="fields"
            onChange={e => onChange(e, filter, [...filter.fields.slice(0, currentLevel), e.value])}
            className="p-inputtext-sm flex-1 max-w-20rem"
          />
        )}
        {needRecurse && renderFieldSelector(filter, filterIndex, currentLevel + 1)}

        {filter.fields[currentLevel]?.leaf && (
          <div className="p-inputgroup p-inputtext-sm flex-1 max-w-30rem">
            {renderInputByType(filter, filter.fields[currentLevel])}
            <Dropdown
              value={filter?.cond}
              options={getOperators(filter.fields[currentLevel])}
              optionLabel="label"
              optionValue="key"
              placeholder="Select One"
              name="cond"
              onChange={e => onChange(e, filter, e.value)}
              className="p-inputgroup-addon p-0"
            />
          </div>
        )}
      </>
    );
  };

  const renderFilters = filters?.map((f, i) => (
    <div
      className="flex flex-column sm:flex-row align-items-baseline w-full gap-3 border-bottom-1 border-gray-300 py-1"
      // eslint-disable-next-line react/no-array-index-key
      key={`${f.key}-${i}`}
    >
      <span className="text-right">{f.label}:</span>
      {renderFieldSelector(f, i + 1)}
      <Button
        className="p-button-text text-red-500"
        icon="pi pi-times"
        onClick={() => setFilters(prev => prev.toSpliced(i, 1))}
      />
    </div>
  ));

  const handleSetFilters = () => {
    const mapFilters = filters.map(f => ({
      cond: f.cond,
      property: f.fields.map(p => p.key).join("."),
      value: f.value,
    }));
    onLazyEvent(prev => ({
      ...prev,
      andFilters: mapFilters,
      internalFilters: filters
    }));

    getNextQuery({
      variables: {
        input: {
          projectId,
          skip: lazyState.first,
          take: lazyState.rows,
          sort: [
            {
              property:
                lazyState.sortField.length === 2 && lazyState.sortField.at(-1) === "value"
                  ? `customFields.${lazyState.sortField[0]}`
                  : lazyState.sortField,
              direction: lazyState.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
            },
          ],
          andFilters: mapFilters,
        },
      },
    });
  }

  return (
    <div className="flex flex-column gap-2 m-3">
      {renderFilters}
      <div className="flex justify-content-between">
        <TreeSelect
          placeholder="Add Filters"
          options={treeOptions}
          className="w-10rem"
          onChange={addFilter}
          filter
          resetFilterOnHide
          scrollHeight="400px"
        />
        <Button label="Apply Filters" className="w-9rem" onClick={handleSetFilters} />
      </div>
    </div>
  );
};

export default TableFilters;